import { useCallback, useRef } from 'react';
import {
  XYPosition,
  NodeChange,
  Node as ReactFlowNode,
  applyNodeChanges,
  NodePositionChange,
  EdgeChange,
  applyEdgeChanges,
} from '@xyflow/react';
import {
  CanvasEdgeConnectionType,
  CanvasElementType,
  SweepNodesChangeEvent,
  SweepNodesChangeMoveGroupEvent,
  SweepNodesChangeType,
} from '../canvas-types';
import { snapPositionToGrid } from '../helpers/gridPositioningUtils';
import { SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { useOnRegularNodePositionChange } from './useOnRegularNodePositionChange';
import { useOnGroupNodePositionChange } from './useOnGroupNodePositionChange';
import { createObjectOperations } from './objectOperations';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import { RFEdgeFloatingEdge } from '../edges';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import uniqBy from 'lodash/uniqBy';

export type NodeTransformations = {
  extraNodePositionChanges: NodePositionChange[];
};

export interface MovingNodesORiginalPositionRef {
  [nodeId: string]: { originalPosition: XYPosition };
}

export const useOnNodeChange = () => {
  const {
    setCanvasNodes: setNodes,
    setCanvasEdges: setEdges,
    getCanvasNodes: getNodes,
  } = useSweepCanvasState();
  const movingNodesRef = useRef<MovingNodesORiginalPositionRef>({});

  // Records the initial position of a node when it starts moving
  const maybeRecordInitialPosition = useCallback((node?: ReactFlowNode) => {
    if (node && !movingNodesRef.current[node.id]) {
      movingNodesRef.current[node.id] = { originalPosition: node.position };
    }
  }, []);

  const { onRegularNodePositionChange } = useOnRegularNodePositionChange(movingNodesRef);
  const { onGroupNodePositionChange } = useOnGroupNodePositionChange(movingNodesRef);
  const { onSweepNodesChange } = useSweepCanvasPropsCtx();

  const onNodesChange = useCallback(
    (nodeChanges: NodeChange<SweepCanvasRfNode>[]) => {
      const positionTransformations: NodePositionChange[] = nodeChanges.filter(
        (nodeChange) => nodeChange.type === 'position',
      );
      if (!positionTransformations.length) {
        setNodes((nodes) => {
          const appliedNodes = applyNodeChanges(nodeChanges, nodes);
          return appliedNodes;
        });
        return;
      }

      const extraNodePositionChanges: NodePositionChange[] = [];

      const nodeTransformations: NodeTransformations = {
        extraNodePositionChanges,
      };

      const nodes = getNodes();

      const nodeOperations = createObjectOperations(nodes);
      const { getObject: getNode } = nodeOperations;

      const _groupChanges: SweepNodesChangeMoveGroupEvent[] = [];
      const _regularChanges: SweepNodesChangeEvent[] = [];

      positionTransformations.forEach((nodeChange) => {
        const node = getNode(nodeChange.id);

        maybeRecordInitialPosition(node);
        if (node && nodeChange.position) {
          if (!nodeChange.dragging) {
            nodeChange.position = snapPositionToGrid(nodeChange.position);
          }

          switch (node.type) {
            case CanvasElementType.REGULAR: {
              const changes = onRegularNodePositionChange({
                nodeChange,
                node,
                nodeTransformations,
                nodeOperations,
              });
              if (changes?.length) {
                _regularChanges.push(...changes);
              }
              break;
            }
            case CanvasElementType.GROUP_LABEL:
            case CanvasElementType.GROUP: {
              const groupChanges = onGroupNodePositionChange({
                nodeChange,
                node,
                nodeTransformations,
                nodeOperations,
              });
              if (groupChanges?.length) {
                _groupChanges.push(...(groupChanges as SweepNodesChangeMoveGroupEvent[]));
              }
              break;
            }
            default:
              break;
          }
        }
      });
      const _groupChangesFromRegularNodeChanges = _regularChanges.filter(
        (change) => change.type === SweepNodesChangeType.MoveGroup,
      );
      const _regularChangesFromRegularNodeChanges = _regularChanges.filter(
        (change) => change.type === SweepNodesChangeType.MoveNode,
      );

      if (_groupChangesFromRegularNodeChanges.length) {
        _groupChanges.push(..._groupChangesFromRegularNodeChanges);
      }

      // Next section merges group and regular changes into a single array of changes
      const allChanges: SweepNodesChangeEvent[] = [];
      if (_groupChanges.length) {
        allChanges.push({
          type: SweepNodesChangeType.MoveGroup,
          change: {
            groupsToMove: uniqBy(
              _groupChanges.map((change) => change.change.groupsToMove).flat(),
              'nodeId',
            ),
          },
        });
      }
      if (_regularChangesFromRegularNodeChanges.length) {
        allChanges.push({
          type: SweepNodesChangeType.MoveNode,
          change: {
            nodesToMove: uniqBy(
              _regularChangesFromRegularNodeChanges
                .map((change) => change.change.nodesToMove)
                .flat(),
              'nodeId',
            ),
          },
        });
      }
      // End of merge section

      if (allChanges.length) {
        onSweepNodesChange?.(allChanges);
      }

      const appliedNodes = applyNodeChanges(
        [...nodeChanges, ...extraNodePositionChanges],
        nodeOperations.getObjects(),
      );
      setNodes(appliedNodes);

      setEdges((edges) => {
        const allDraggingNodesIds = [...positionTransformations, ...extraNodePositionChanges]
          .filter((nodeChange) => nodeChange.dragging)
          .map((nodeChange) => nodeChange.id);

        return edges.map((edge) => {
          if (!edge.data) {
            return edge;
          }
          const connectedNodesDragging =
            allDraggingNodesIds.includes(edge.source) || allDraggingNodesIds.includes(edge.target);

          if (connectedNodesDragging) {
            return {
              ...edge,
              data: {
                ...edge.data,
                connectedNodesDragging,
                connectionType: CanvasEdgeConnectionType.Bezier,
              },
            };
          } else {
            if (edge.data.connectedNodesDragging) {
              return {
                ...edge,
                data: {
                  ...edge.data,
                  connectedNodesDragging: false,
                },
              };
            }
          }
          return edge;
        });
      });
    },
    [
      getNodes,
      setNodes,
      setEdges,
      maybeRecordInitialPosition,
      onRegularNodePositionChange,
      onSweepNodesChange,
      onGroupNodePositionChange,
    ],
  );

  const onEdgesChange = useCallback(
    (edgeChanges: EdgeChange<RFEdgeFloatingEdge>[]) => {
      setEdges((edges) => applyEdgeChanges(edgeChanges, edges));
    },
    [setEdges],
  );

  return {
    onNodesChange,
    onEdgesChange,
  };
};
